<template>
	<div class="fixed inset-0 z-30 flex items-center justify-center p-4 bg-black/40" @click="emit('close')">
		<div
			ref="dialog"
			role="dialog"
			aria-modal="true"
			aria-labelledby="choice-modal-title"
			tabindex="-1"
			class="relative w-full max-w-md p-6 bg-white rounded-lg outline-none"
			@click.stop
		>
			<p id="choice-modal-title" class="font-serif text-xl text-neutral-950 mb-4">{{ t('make_your_choice') }}</p>

			<div class="flex flex-col divide-y divide-neutral-100 border border-neutral-100 rounded-lg">
				<button
					v-for="choice in choices"
					:key="choice.id"
					type="button"
					:aria-pressed="selected?.id === choice.id"
					:class="[
            'flex items-center justify-between gap-2 px-2 sm:px-4 py-2 min-h-12',
            {
              'text-neutral-600': selected && selected.id !== choice.id,
              'text-black': selected && selected.id === choice.id,
            },
          ]"
					@click="selected = choice"
				>
					<span class="flex items-center gap-4">
						<span
							:class="[
                'flex flex-none items-center justify-center w-4 h-4 border rounded-full border-current after:h-2.5 after:w-2.5 after:rounded-full after:bg-current',
                {
                  'text-gold-600 after:opacity-0': selected && selected.id !== choice.id,
                  'text-gold-500': selected && selected.id === choice.id
                },
              ]"
						/>
						<span class="text-sm text-left">{{ choice.name }}</span>
					</span>

					<PriceLine :price-data="choice.price_data" :selected-date class="text-sm" />
				</button>
			</div>

			<div class="flex items-center justify-between mt-6">
				<button type="button" class="flex items-center gap-2 text-sm uppercase tracking-widest" @click="emit('close')">
					<IconChevronLeft class="size-4 flex-none" />
					{{ t('go_back') }}
				</button>
				<button type="button" class="btn-primary h-10 px-6 text-xs" @click="emit('book', selected)">
					{{ t('choose') }}
				</button>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import IconChevronLeft from '@thermenresorts/icons/svg/chevron-left.svg';
import { BuyableChoice } from '../api/resources';
import PriceLine from './PriceLine.vue';

export type Props = {
  choices: BuyableChoice[];
  selectedDate: string;
};

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: 'close'): void;
  (event: 'book', selected: BuyableChoice): void;
}>();

const { t } = useI18n();

const selected = ref<BuyableChoice>();

const dialog = ref<HTMLElement>();

let previouslyFocused: Element | null = null;

const onKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') emit('close');
};

onMounted(() => {
  selected.value = props.choices[0];

  previouslyFocused = document.activeElement;
  dialog.value?.focus();
  document.addEventListener('keydown', onKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);

  if (previouslyFocused instanceof HTMLElement) {
    previouslyFocused.focus();
  }
});
</script>
